import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Route, MapPin, Mountain, Clock, AlertCircle, Crown, CheckCircle, Upload } from 'lucide-react';
import './CreateRoutePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateRoutePage() {
  const { t } = useTranslation();
  const { user, token, isPremium } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    activity_type: 'cycling',  // NEW: cycling, hiking, roadtrip
    region: 'Highlands',
    difficulty: 'moderate',
    distance_km: '',
    elevation_gain_m: '',
    route_type: 'road',
    bike_type: 'all',
    duration_hours: '',
    tags: []
  });
  
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showGpxImport, setShowGpxImport] = useState(false);
  const [gpxFile, setGpxFile] = useState(null);

  if (!user) {
    return (
      <div className="create-route-page">
        <div className="auth-required">
          <AlertCircle size={48} />
          <h2>Anmeldung erforderlich</h2>
          <p>Bitte melde dich an, um Routen zu erstellen.</p>
          <Link to="/login" className="login-button">
            Jetzt anmelden
          </Link>
        </div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="create-route-page">
        <div className="premium-required">
          <Crown size={64} />
          <h2>Premium Feature</h2>
          <p>Das Erstellen eigener Routen ist nur für Premium-Mitglieder verfügbar.</p>
          <ul>
            <li>✅ Eigene Routen erstellen und teilen</li>
            <li>✅ Unbegrenzter Zugriff auf alle Routen</li>
            <li>✅ Fotos hochladen</li>
            <li>✅ Und vieles mehr...</li>
          </ul>
          <Link to="/profile" className="upgrade-button">
            Jetzt auf Premium upgraden
          </Link>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Create a sample coordinate path (in production, user would draw on map)
      const mockCoordinates = [
        [56.5, -4.2],
        [56.52, -4.22],
        [56.54, -4.24]
      ];

      const routeData = {
        ...formData,
        distance_km: parseFloat(formData.distance_km),
        elevation_gain_m: parseInt(formData.elevation_gain_m),
        duration_hours: parseFloat(formData.duration_hours),
        coordinates: mockCoordinates
      };

      await axios.post(`${API}/routes`, routeData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/routes');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Fehler beim Erstellen der Route');
    } finally {
      setLoading(false);
    }
  };

  const handleGpxImport = async () => {
    if (!gpxFile) return;

    setError('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', gpxFile);

      const response = await axios.post(`${API}/import-gpx`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Pre-fill form with GPX data
      const gpxData = response.data.route_data;
      setFormData({
        ...formData,
        name: gpxData.name,
        description: gpxData.description,
        distance_km: gpxData.distance_km,
        elevation_gain_m: gpxData.elevation_gain_m,
        duration_hours: gpxData.duration_hours,
        tags: gpxData.tags
      });

      alert(response.data.message + '\n\n' + response.data.moderation_note);
      setShowGpxImport(false);
      setGpxFile(null);
    } catch (error) {
      setError(error.response?.data?.detail || 'Fehler beim GPX-Import');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="create-route-page">
        <div className="success-message">
          <CheckCircle size={64} />
          <h2>Route erfolgreich erstellt!</h2>
          <p>Du wirst zur Routen-Übersicht weitergeleitet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-route-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">
            <Route size={32} />
            Eigene Route erstellen
          </h1>
          <p className="page-subtitle">Teile deine Lieblingsroute mit der Community</p>
        </div>
        <div className="premium-badge-header">
          <Crown size={20} />
          Premium Feature
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="gpx-import-section">
        <button 
          type="button"
          onClick={() => setShowGpxImport(!showGpxImport)}
          className="btn-gpx-import"
        >
          <Upload size={20} />
          {showGpxImport ? 'GPX Import schließen' : 'GPX-Datei importieren'}
        </button>

        {showGpxImport && (
          <div className="gpx-import-box">
            <h3>📁 GPX-Datei hochladen</h3>
            <p>Lade deine GPX-Datei hoch und die Route wird automatisch ausgefüllt.</p>
            <input
              type="file"
              accept=".gpx"
              onChange={(e) => setGpxFile(e.target.files[0])}
              className="gpx-file-input"
            />
            {gpxFile && (
              <div className="gpx-file-info">
                ✅ {gpxFile.name} ausgewählt
              </div>
            )}
            <button 
              type="button"
              onClick={handleGpxImport}
              disabled={!gpxFile || loading}
              className="btn-import"
            >
              {loading ? 'Importiere...' : 'Jetzt importieren'}
            </button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="create-route-form">
        <div className="form-section">
          <h3>Grundlegende Informationen</h3>
          
          <div className="form-group">
            <label htmlFor="name">Routenname *</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="z.B. Loch Ness Rundweg"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Beschreibung *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Beschreibe die Route..."
              rows={4}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="activity_type">Aktivität *</label>
              <select id="activity_type" name="activity_type" value={formData.activity_type} onChange={handleChange}>
                <option value="cycling">🚴 Radfahren</option>
                <option value="hiking">🥾 Wandern</option>
                <option value="roadtrip">🚗 Roadtrip</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="region">Region *</label>
              <select id="region" name="region" value={formData.region} onChange={handleChange}>
                <option value="Highlands">Highlands</option>
                <option value="Islands">Islands</option>
                <option value="Borders">Borders</option>
                <option value="Cities">Cities</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="difficulty">Schwierigkeit *</label>
              <select id="difficulty" name="difficulty" value={formData.difficulty} onChange={handleChange}>
                <option value="easy">Leicht</option>
                <option value="moderate">Mittel</option>
                <option value="hard">Schwer</option>
                <option value="expert">Experte</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Details</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="distance_km">
                <MapPin size={18} />
                Distanz (km) *
              </label>
              <input
                id="distance_km"
                name="distance_km"
                type="number"
                step="0.1"
                value={formData.distance_km}
                onChange={handleChange}
                placeholder="25.5"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="elevation_gain_m">
                <Mountain size={18} />
                Höhenmeter (m) *
              </label>
              <input
                id="elevation_gain_m"
                name="elevation_gain_m"
                type="number"
                value={formData.elevation_gain_m}
                onChange={handleChange}
                placeholder="450"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="duration_hours">
                <Clock size={18} />
                Dauer (Std.) *
              </label>
              <input
                id="duration_hours"
                name="duration_hours"
                type="number"
                step="0.5"
                value={formData.duration_hours}
                onChange={handleChange}
                placeholder="3.5"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="route_type">Routentyp *</label>
              <select id="route_type" name="route_type" value={formData.route_type} onChange={handleChange}>
                <option value="road">Straße</option>
                <option value="gravel">Schotter</option>
                <option value="mountain">Berg</option>
                <option value="mixed">Gemischt</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="bike_type">Fahrradtyp *</label>
              <select id="bike_type" name="bike_type" value={formData.bike_type} onChange={handleChange}>
                <option value="all">Alle</option>
                <option value="normal">Normal</option>
                <option value="ebike">E-Bike</option>
                <option value="mountain">Mountainbike</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Tags (optional)</h3>
          <p className="help-text">Füge Schlagwörter hinzu, um die Route besser auffindbar zu machen</p>
          
          <div className="tag-input-container">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="z.B. whisky, castle, coastal"
            />
            <button type="button" onClick={addTag} className="add-tag-btn">
              Hinzufügen
            </button>
          </div>

          {formData.tags.length > 0 && (
            <div className="tags-list">
              {formData.tags.map(tag => (
                <span key={tag} className="tag">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/routes')} className="cancel-button">
            Abbrechen
          </button>
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Wird erstellt...' : 'Route erstellen'}
          </button>
        </div>
      </form>
    </div>
  );
}
